import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Spacing, fontFamily } from '@/shared/theme';
import { Button, Text, Title, CenteredColumn } from '@/shared/ui';
import ScaleOption from '@/shared/ScaleOption';
import { QuestionProgress } from '@/components/QuestionProgress';
import { BorisScreener } from '@/shared/borisScreenerTheme';
import { StartOverModal } from '@/components/StartOverModal';
import { useRegistration } from '@/context/RegistrationContext';
import { useAnalytics } from '@/hooks/useAnalytics';
import { useAppFlow, STEP_KEYS } from '@/hooks/useAppFlow';
import { useEligibilityFinalize } from '@/hooks/useEligibilityFinalize';
import { GAD7_QUESTION_COUNT, sumAnswers, meetsBHThreshold } from '@/utils/bhScreener';
import { VIEW_GAD7_SCREENER, GAD7_SCREENER_COMPLETED, BH_TRACK_DROPPED_THRESHOLD } from '@/consts/events';

// 4-point GAD-7 answer scale (labels come from i18n gad7.a0-a3). No default selection.
const ANSWER_SCALE = [0, 1, 2, 3];

type Phase = 'questions' | 'supportive' | 'dropped';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  gap: ${Spacing.x3}px;
  position: relative;
  z-index: 1;
`;

// Left-aligned content column, matching Boris's mobile composition.
const Content = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
`;

// Faint monospace question counter (Boris's rg-qcount).
const QuestionCount = styled.div`
  font-family: ui-monospace, 'SF Mono', Menlo, monospace;
  font-size: 12px;
  letter-spacing: 0.08em;
  /* inkFaint fails WCAG AA (2.76:1) for this real content; inkSoft passes (5.76:1). */
  color: ${BorisScreener.inkSoft};
  margin-bottom: ${Spacing.x1}px;
`;

// Small muted prompt (Boris's rg-sub) — replaces the large centered heading.
const SubHeading = styled.div`
  font-family: ${fontFamily};
  font-size: 16px;
  line-height: 1.35;
  color: ${BorisScreener.inkSoft};
`;

const QuestionText = styled(Text)`
  margin-top: ${Spacing.x1_5}px;
  font-size: 22px;
  font-weight: 500;
  color: ${BorisScreener.ink};

  /* Boris .reg-dt .rg-scale-q scales the question up on desktop. */
  @media (min-width: 900px) {
    font-size: 26px;
  }
`;

const OptionsList = styled.div`
  width: 100%;
  max-width: 560px;
  margin-top: ${Spacing.x2}px;
  display: flex;
  flex-direction: column;
  gap: ${Spacing.x1}px;
`;

const Footer = styled.div`
  margin-top: ${Spacing.x3}px;
  width: 100%;
  display: flex;
  justify-content: center;
`;

/**
 * RND-35692 — GAD-7 anxiety screener. Presents the 7 CMS-worded items one at a
 * time on a 0-3 scale, then finalizes the behavioral-health screener:
 *  - stores the GAD-7 answers/score (client-side only, never submitted),
 *  - shows the supportive safety message (PRD 11 §4.1) when the BH threshold is
 *    met — UNLESS the Q9 crisis gate already fired on PHQ-9 (that urgent message
 *    takes precedence),
 *  - shows a neutral "thanks for completing" message (no score) when below
 *    threshold (PRD 11 §4.1 AC),
 *  - then finalizes the track assignment + routing (the CMS eligibility check
 *    already ran up front — no submission here; RND-36523).
 *
 * OPEN QUESTION (Dotan/Edo, PR #78): when crisisGateShown is true (Q9 endorsed
 * on PHQ-9) but the combined score still falls below threshold, this shows the
 * neutral "dropped" message + fires BH_TRACK_DROPPED_THRESHOLD. Not a missing
 * safety-message gap — the crisis block already fired on PHQ-9 — but whether
 * that's the intended outcome (vs. the supportive message overriding whenever
 * the crisis gate fired, regardless of threshold) is a product/clinical call,
 * not decided here.
 *
 * The BH threshold gates the BH track: determineEligibility drops BH when the
 * screener is below threshold (PRD 01 v0.7 §6.4, confirmed by Dotan 2026-07-15),
 * so a sub-threshold user is not offered or aligned to BH (BH-only becomes the
 * not-eligible terminal). The score itself is never shown to the user; here it
 * only picks the local supportive-vs-neutral message. The ICD-10 override
 * (PRD §9 id-10, Edo + Jessica) is still open and would only ever re-include BH,
 * never undo this drop.
 */
const GAD7ScreenerStep: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { registrationData, updateBHScreener } = useRegistration();
  const { trackView, track } = useAnalytics();
  const { finalizeAndRoute } = useEligibilityFinalize();
  const [showStartOverModal, setShowStartOverModal] = useState(false);
  const { resetBlocker } = useAppFlow(STEP_KEYS.GAD7_SCREENER, {
    onBackNavigationBlocked: () => setShowStartOverModal(true),
  });

  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState<(number | null)[]>(() => Array(GAD7_QUESTION_COUNT).fill(null));
  const [phase, setPhase] = useState<Phase>('questions');

  useEffect(() => {
    trackView({ event: VIEW_GAD7_SCREENER });
  }, [trackView]);

  // RND-36523: the CMS eligibility check already ran up front — there is no
  // submission here. Mark the behavioral-health screener complete and finalize
  // the track assignment (CMS-eligible ∩ condition-confirmed). determineEligibility
  // applies the PRD §6.4 sub-threshold BH drop via the pure meetsBHThreshold
  // predicate, so a below-threshold score removes BH without any CMS call.
  const completeAndFinalize = () => {
    updateBHScreener({ completed: true });
    finalizeAndRoute();
  };

  const handlePick = (value: string) => {
    const nextAnswers = answers.slice();
    nextAnswers[index] = Number(value);
    setAnswers(nextAnswers);

    if (index + 1 < GAD7_QUESTION_COUNT) {
      setIndex(index + 1);
      return;
    }

    // Last question answered — finalize the screener.
    const completeAnswers = nextAnswers as number[];
    const gad7Score = sumAnswers(completeAnswers);
    const phq9Score = registrationData.bhScreener.phq9Score ?? 0;
    const meets = meetsBHThreshold(phq9Score, gad7Score);

    // Client-side only — raw answers/score are never submitted (RND-35692).
    // `completed` is set separately, only once runSubmit's CMS submission
    // actually succeeds (see runSubmit).
    updateBHScreener({ gad7Answers: completeAnswers, gad7Score });

    // PHI: no raw score prop. TODO(RND-35692): restore once PHI review clears (Daniel).
    track({ event: GAD7_SCREENER_COMPLETED });
    if (!meets) {
      track({ event: BH_TRACK_DROPPED_THRESHOLD });
    }

    // Supportive message when above threshold — but the Q9 urgent message (shown
    // on PHQ-9) takes precedence, so don't double up.
    if (meets) {
      if (!registrationData.bhScreener.crisisGateShown) {
        setPhase('supportive');
        return;
      }
      completeAndFinalize();
      return;
    }

    // Below threshold — neutral message, no score (PRD 11 §4.1 AC).
    setPhase('dropped');
  };

  const handleStartOver = () => {
    setShowStartOverModal(false);
    navigate('/');
  };

  const startOverModal = (
    <StartOverModal
      isOpen={showStartOverModal}
      onConfirm={handleStartOver}
      onCancel={() => {
        setShowStartOverModal(false);
        resetBlocker();
      }}
    />
  );

  if (phase === 'supportive') {
    return (
      <Container>
        <CenteredColumn span={8}>
          {/* PRD 11 §4.1 supportive message — no score shown. */}
          <Text size="p1" centered>
            {t('bh-support.body')}
          </Text>
          <Footer>
            <CenteredColumn span={6}>
              <Button text={t('bh-support.continue')} onClick={completeAndFinalize} variant="brand-primary" />
            </CenteredColumn>
          </Footer>
        </CenteredColumn>
        {startOverModal}
      </Container>
    );
  }

  if (phase === 'dropped') {
    return (
      <Container>
        <CenteredColumn span={8}>
          {/* PRD 11 §4.1 — neutral acknowledgement, no score shown. */}
          <Title size="h4" centered>
            {t('bh-dropped.title')}
          </Title>
          <Text size="p1" centered>
            {t('bh-dropped.body')}
          </Text>
          <Footer>
            <CenteredColumn span={6}>
              <Button text={t('general.continue')} onClick={completeAndFinalize} variant="brand-primary" />
            </CenteredColumn>
          </Footer>
        </CenteredColumn>
        {startOverModal}
      </Container>
    );
  }

  return (
    <Container>
      <CenteredColumn span={8}>
        <Content>
          <QuestionProgress total={GAD7_QUESTION_COUNT} current={index} />
          <QuestionCount>
            {t('gad7.question-count', { current: index + 1, total: GAD7_QUESTION_COUNT })}
          </QuestionCount>
          <SubHeading>{t('gad7.heading')}</SubHeading>
          <QuestionText size="p1">
            {t(`gad7.q${index + 1}`)}
          </QuestionText>

          {/* WCAG: single-select group; one GAD-7 item at a time, no default. */}
          <OptionsList role="radiogroup" aria-label={t(`gad7.q${index + 1}`)}>
            {ANSWER_SCALE.map((value) => (
              <ScaleOption
                key={value}
                value={String(value)}
                label={t(`gad7.a${value}`)}
                selected={answers[index] === value}
                onClick={handlePick}
              />
            ))}
          </OptionsList>
        </Content>
      </CenteredColumn>

      {startOverModal}
    </Container>
  );
};

export { GAD7ScreenerStep };
